import { useEffect, useRef } from "react";
import { Observable, Subject, merge } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, map, switchMap } from "rxjs/operators";

export interface UseLinkAutoParseOptions {
  debounceMs?: number;
}

export interface UseLinkAutoParseReturn {
  submit: (url: string) => void;
  submitImmediately: (url: string) => void;
}

/**
 * 链接输入框的 RxJS 自动解析编排：
 * - `submit` 走 debounce，给 onChange 用；`submitImmediately` 不 debounce，给 onPressEnter 用
 * - switchMap 取消上一次未完成的订阅，旧请求的 AbortSignal 会被触发；最终结果以最后一次为准
 * - 空串过滤，其它一律下发，URL 合法性由后端判断
 *
 * 父组件的 `onParse` 必须在 await 之后、副作用之前检查 `signal.aborted`，
 * 否则旧请求即便被 switchMap unsubscribe，副作用仍会执行。
 */
export const useLinkAutoParse = (
  onParse: (url: string, signal: AbortSignal) => Promise<unknown>,
  { debounceMs = 450 }: UseLinkAutoParseOptions = {},
): UseLinkAutoParseReturn => {
  const onParseRef = useRef(onParse);
  onParseRef.current = onParse;

  const subjectsRef = useRef<{ debounced: Subject<string>; immediate: Subject<string> } | null>(null);
  if (!subjectsRef.current) {
    subjectsRef.current = {
      debounced: new Subject<string>(),
      immediate: new Subject<string>(),
    };
  }

  useEffect(() => {
    const subjects = subjectsRef.current;
    if (!subjects) return;
    const sub = merge(subjects.debounced.pipe(debounceTime(debounceMs)), subjects.immediate)
      .pipe(
        map((v) => v.trim()),
        filter((v): v is string => Boolean(v)),
        distinctUntilChanged(),
        switchMap(
          (url) =>
            new Observable<void>((observer) => {
              const ctrl = new AbortController();
              let cancelled = false;
              onParseRef
                .current(url, ctrl.signal)
                .then(() => {
                  if (!cancelled) observer.complete();
                })
                .catch(() => {
                  if (!cancelled) observer.complete();
                });
              return () => {
                cancelled = true;
                ctrl.abort();
              };
            }),
        ),
      )
      .subscribe();
    return () => sub.unsubscribe();
  }, [debounceMs]);

  return {
    submit: (url) => subjectsRef.current?.debounced.next(url),
    submitImmediately: (url) => subjectsRef.current?.immediate.next(url),
  };
};
